import React, { useCallback, useEffect, useState } from 'react';
import { useScreenClass } from '@vp/swan';
import { useSelector } from 'react-redux';
import useSiteData from '../../../hooks/useSiteData';
import { DynamicExpressCheckout } from '../../shared/expressCheckout/DynamicExpressCheckout';
import useExperiment from '../../../hooks/useExperiment';
import { cartSelectors } from '../../../state/cart';
import useIdentityContext from '../../../hooks/useIdentityContext';
import { useExpressCheckoutContext } from '../../contexts/ExpressCheckoutContext';
import { trackNavigationClicked } from '../../../apis/trackingApi';
import type { CheckoutGatewayProps, RootState } from '../../../types';
import { cartItemSelectors } from '../../../state/cartItems';
import {
  BILLING_ADDRESS_VALIDITY_CHECK_KEY,
  CONSIGNEE_ADDRESS_VALIDITY_CHECK_KEY,
  SHIPPING_ADDRESS_VALIDITY_CHECK_KEY,
  TAX_ID_PRESENT_VALIDITY_CHECK_KEY,
} from '../../../constants/checkoutGateway';

/**
 * Initially for use during a small-screen Express Checkout a/b test, the Checkout Gateway manages the routing for the
 * checkout CTA functionality. If a user meets a certain criteria, they are presented with Express Checkout. And, if
 * not, they are redirected to CheckoutUI per normal functionality. Each time the CTA is clicked, this component will
 * be mounted.
 * @param trackingCtaValue
 * @param resetCheckoutGateway
 * @constructor
 */
export const CheckoutGateway = ({
  trackingCtaValue,
  resetCheckoutGateway,
}: CheckoutGatewayProps) => {
  const screenClass = useScreenClass();

  const isReadyToEnterCheckout = useSelector((state: RootState) =>
    cartSelectors.selectReadyToEnterCheckout(state.cart)
  );
  const invalidValidityChecks = useSelector((state: RootState) =>
    cartSelectors.selectInvalidValidityChecks(state.cart)
  );
  const cartLegalRequirements = useSelector((state: RootState) =>
    cartSelectors.selectLegalRequirements(state.cart)
  );
  const itemsWithLegalRequirements = useSelector((state: RootState) =>
    cartItemSelectors.selectItemsWithLegalRequirements(state)
  );

  const {
    identity: { isSignedIn },
  } = useIdentityContext();

  const { isDynamicLoadFailure } = useExpressCheckoutContext();

  const hasSmallScreen = screenClass === 'xs';
  const legalRequirementsPresent =
    itemsWithLegalRequirements?.length || cartLegalRequirements?.length;
  const requiresValidConsigneeAddress = invalidValidityChecks.includes(
    CONSIGNEE_ADDRESS_VALIDITY_CHECK_KEY
  );
  const hasValidShippingAndBillingAddress =
    !invalidValidityChecks.includes(BILLING_ADDRESS_VALIDITY_CHECK_KEY) &&
    !invalidValidityChecks.includes(SHIPPING_ADDRESS_VALIDITY_CHECK_KEY);
  const requiresTaxId = invalidValidityChecks.includes(
    TAX_ID_PRESENT_VALIDITY_CHECK_KEY
  );

  const shopperStateAllowsExpressCheckout = !!(
    isReadyToEnterCheckout &&
    isSignedIn &&
    hasSmallScreen &&
    hasValidShippingAndBillingAddress &&
    !requiresTaxId &&
    !legalRequirementsPresent &&
    !requiresValidConsigneeAddress
  );

  const { externalAppNavigation } = useSiteData();
  const useExpressCheckout = useExperiment(
    'express_checkout_on_mobile_checkout_cta',
    'variation_2',
    shopperStateAllowsExpressCheckout
  );

  const [showExpressCheckoutFlyout, setShowExpressCheckoutFlyout] =
    useState<boolean>(useExpressCheckout);

  const handleRedirect = useCallback(() => {
    trackNavigationClicked({
      sourcePagePath: externalAppNavigation.cart,
      destinationPagePath: externalAppNavigation.checkout,
      ctaValue: trackingCtaValue,
    });
    window.location.assign(externalAppNavigation.checkout);
  }, [externalAppNavigation, trackingCtaValue]);

  useEffect(() => {
    if (useExpressCheckout && !showExpressCheckoutFlyout) {
      resetCheckoutGateway();
    }
  }, [showExpressCheckoutFlyout, useExpressCheckout, resetCheckoutGateway]);

  useEffect(() => {
    if (!useExpressCheckout) {
      handleRedirect();
    }
  }, [handleRedirect, useExpressCheckout]);

  useEffect(() => {
    if (isDynamicLoadFailure) {
      handleRedirect();
    }
  }, [handleRedirect, isDynamicLoadFailure]);

  return useExpressCheckout ? (
    <DynamicExpressCheckout
      showExpressCheckoutFlyout={showExpressCheckoutFlyout}
      setShowExpressCheckoutFlyout={setShowExpressCheckoutFlyout}
    />
  ) : null;
};
